import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { BiLinkAlt } from "react-icons/bi";

import { getJrLineDetail } from "../job-request/jrV2Slice";
import { GS_FORM, calcBilled } from "./GeneralServiceEntryEditor";
import { formatQty } from "./QtyInput";
import FotoBuktiLink, { adaFotoAkomodasi } from "./FotoBukti";


const STATUS_STYLE = {
  pending:  { label: "Pending",  cls: "bg-gray-100 text-gray-500" },
  approved: { label: "Approved", cls: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", cls: "bg-red-100 text-red-700" },
};
function StatusPill({ status }) {
  const c = STATUS_STYLE[status] ?? STATUS_STYLE.pending;
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.cls}`}>{c.label}</span>;
}

// Tampilan detail General Service: satu KARTU per layanan (bukan tabel-di-
// dalam-tabel seperti sebelumnya) — header item, tabel entri harian penuh,
// lalu subtotal di kaki kartu. Semua line di-fetch sekali di awal.
export default function GeneralServiceSummary({
  lines,
  rejectMode = false,
  rejectSel = {}, setRejectSel = () => {},
  rejectMap = {}, setRejectMap = () => {},
}) {
  const dispatch = useDispatch();
  const [detailMap, setDetailMap] = useState({}); // { JrLineID: { loading, entries } }

  useEffect(() => {
    lines.forEach(l => {
      if (detailMap[l.JrLineID]) return;
      setDetailMap(m => ({ ...m, [l.JrLineID]: { loading: true, entries: [] } }));
      dispatch(getJrLineDetail(l.JrLineID)).unwrap()
        .then(res => setDetailMap(m => ({ ...m, [l.JrLineID]: { loading: false, entries: res?.data ?? [] } })))
        .catch(() => setDetailMap(m => ({ ...m, [l.JrLineID]: { loading: false, entries: [] } })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.map(l => l.JrLineID).join(",")]);

  const renderVal = (col, e, uom, itemLabel) => {
    switch (col.type) {
      case "label":      return itemLabel;
      case "uom":        return uom;
      case "check":      return e.HouseKeeping ? "✓" : "—";
      case "roomselect": return e.RoomRef || "—";
      case "text":       return e[col.key] || "—";
      case "link":
        if (adaFotoAkomodasi(e.EvidenceUrl)) return <FotoBuktiLink value={e.EvidenceUrl} />;
        return e.EvidenceUrl ? (
          <a href={e.EvidenceUrl} target="_blank" rel="noreferrer"
            className="text-purple-600 hover:text-purple-800 inline-flex items-center gap-1">
            <BiLinkAlt /> lihat
          </a>
        ) : "—";
      case "number":     return e[col.key] === "" || e[col.key] == null ? "—" : formatQty(e[col.key]);
      default:           return e[col.key] || "—"; // date
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {lines.map(l => {
        const uom = l.SatuanNama || l.SatuanID || "—";
        const itemLabel = l.ItemDisplay || l.ItemName || "—";
        const cfg = GS_FORM[l.SubType] || GS_FORM.OTHERS;
        const d = detailMap[l.JrLineID];
        const entries = d?.entries ?? [];
        const rejected = l.ItemStatus === "rejected";
        // Subtotal: jumlah yang sama seperti dihitung backend (MAX/CEIL per
        // formula), dijumlahkan seluruh entri — sama persis dengan Σ Jumlah
        // yang tampil di kaki editor saat aktivitas ini masih diisi.
        const subtotal = entries.reduce((s, e) => s + calcBilled(cfg.formula, e), 0);
        return (
          <div key={l.JrLineID}
            className={`rounded-xl border overflow-hidden ${rejected ? "border-red-300 bg-red-50/20" : "border-gray-200 bg-white"}`}>
            <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-gray-100">
              <label className="flex items-start gap-2 cursor-pointer">
                {rejectMode && (
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-red-600 cursor-pointer"
                    checked={!!rejectSel[l.JrLineID]}
                    onChange={e => setRejectSel(s => ({ ...s, [l.JrLineID]: e.target.checked }))} />
                )}
                <span className={`font-semibold text-sm leading-snug ${rejected ? "text-red-700" : "text-gray-800"}`}>
                  {itemLabel}
                  {l.SubType && <span className="ml-2 text-xs font-normal text-gray-400">({l.SubType})</span>}
                </span>
              </label>
              <StatusPill status={l.ItemStatus} />
            </div>

            {rejected && l.RejectReason && (
              <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-red-100 border border-red-200 text-xs text-red-700">
                <span className="font-semibold">Alasan tolak:</span> {l.RejectReason}
              </div>
            )}
            {rejectMode && rejectSel[l.JrLineID] && (
              <div className="px-4 pt-3">
                <input type="text" placeholder="Alasan tolak (wajib diisi)"
                  className="w-full border border-red-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-red-500"
                  value={rejectMap[l.JrLineID] ?? ""}
                  onChange={e => setRejectMap(m => ({ ...m, [l.JrLineID]: e.target.value }))} />
              </div>
            )}

            <div className="px-4 py-3">
              {!d || d.loading ? (
                <div className="text-xs text-gray-400 py-1">Memuat entri…</div>
              ) : entries.length === 0 ? (
                <div className="text-xs text-gray-400 py-1">Belum ada entri.</div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-left text-gray-400">
                        {cfg.cols.map(c => <th key={c.key} className="px-2 py-1.5 font-medium whitespace-nowrap">{c.label}</th>)}
                        <th className="px-2 py-1.5 font-medium text-right">Jumlah</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {entries.map((e, i) => (
                        <tr key={i}>
                          {cfg.cols.map(c => (
                            <td key={c.key} className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                              {renderVal(c, e, uom, itemLabel)}
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right text-gray-700">{formatQty(calcBilled(cfg.formula, e))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-sm">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-semibold text-gray-800">
                Σ {formatQty(subtotal)}{uom !== "—" ? ` ${uom}` : ""}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
